<template>
  <div :class="classResolver(classes)" class="greeting-tile">
    <div class="greeting-tile__text">
      <h3>{{ resolvedTitle }}</h3>
      <p v-if="subtitle">{{ subtitle }}</p>
    </div>

    <div v-if="showSnapshot && (snapshotLoading || snapshotChips.length)" class="greeting-tile__snapshot">
      <CygnusIcon v-if="snapshotLoading" name="spinner" spin />
      <template v-else>
        <div v-for="chip in snapshotChips" :key="chip.key" class="greeting-tile__chip">
          <span class="greeting-tile__chip-k">{{ chip.label }}</span>
          <span class="greeting-tile__chip-v">{{ chip.value ?? '—' }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import classResolver from '@apexl/icarus-core/src/services/classResolver';
import useApi from '@apexl/icarus-core/src/services/api';
import CygnusIcon from '@apexl/icarus-core/src/components/icons/CygnusIcon.vue';

// Icarus counterpart of `apexl\Io\modules\dynamicComponents\Component\Tiles\GreetingTile`
// (registry NAME `GreetingTile`). Per that class's docblock, the Vue side
// resolves the user's name + greeting time-of-day itself (`/api/v1/shell/me`,
// the same "shell.me" endpoint the LHS account card uses).
//
// `showSnapshot` fetches `GET /api/v1/me/greeting-snapshot`
// (io-dynamic-components 1.0.3+), which fans out to the
// `dashboard.collectGreetingItems` hook. This project's contribution is
// `app\module\shell\Hook\RegisterGoshawkGreetingItemsHook`, which reuses
// the same four `shell.platform.*` data sources the StatTile row already
// reads (Mode / Tenant / Modules / Installer) — previously duplicated here
// client-side as a stopgap (see git history) while the endpoint didn't
// exist; now that it does, this is a plain fetch-and-render, same as
// QuickLinksTile's own `/api/v1/me/quick-links` call. No contributions is
// a normal state (`{"items": []}`) — the snapshot row simply doesn't
// render, it isn't an error.
const props = defineProps({
  ...commonProps,
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  showSnapshot: {
    type: Boolean,
    default: false,
  },
});

// `@apexl/icarus-core`'s `useApi()` axios instances carry no request
// timeout of their own — see `useDataSource.js` for the full rationale.
// This tile fires two of these calls on every mount, alongside every other
// dashboard tile, so an explicit timeout keeps a stalled connection from
// hanging the greeting row (and the page) forever.
const REQUEST_TIMEOUT_MS = 8000;

const api = useApi();
const firstName = ref('');
const snapshotItems = ref([]);
const snapshotLoading = ref(true);

function timeOfDayGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return 'Good morning';
  }

  if (hour < 18) {
    return 'Good afternoon';
  }

  return 'Good evening';
}

const resolvedTitle = computed(() => {
  if (props.title) {
    return props.title;
  }

  return firstName.value
    ? `${timeOfDayGreeting()}, ${firstName.value}.`
    : `${timeOfDayGreeting()}.`;
});

onMounted(async () => {
  try {
    const { data } = await api.get('/api/v1/shell/me', { timeout: REQUEST_TIMEOUT_MS });
    firstName.value = data?.user?.first_name ?? '';
  } catch (error) {
    // Non-fatal — the tile still shows a time-of-day greeting with no name.
  }

  if (!props.showSnapshot) {
    snapshotLoading.value = false;
    return;
  }

  try {
    const { data } = await api.get('/api/v1/me/greeting-snapshot', { timeout: REQUEST_TIMEOUT_MS });
    snapshotItems.value = Array.isArray(data?.items) ? data.items : [];
  } catch (error) {
    snapshotItems.value = [];
  } finally {
    snapshotLoading.value = false;
  }
});

const snapshotChips = computed(() => snapshotItems.value.map((item) => ({
  key: item.key,
  label: item.label,
  value: item.value,
})));
</script>

<style lang="scss">
.greeting-tile {
  padding: 20px 24px;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-card);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  &__text {
    h3 {
      margin: 0 0 5px;
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--theme-text-primary);
    }

    p {
      margin: 0;
      font-size: 0.8rem;
      color: var(--theme-text-secondary);
    }
  }

  &__snapshot {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__chip {
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    padding: 7px 11px;
    min-width: 96px;
  }

  &__chip-k {
    display: block;
    font-size: 0.63rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--theme-text-tertiary);
    font-weight: 600;
  }

  &__chip-v {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--theme-text-primary);
  }
}
</style>
